import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { cloneArray } from "../../utils/undoSnapshots";

export interface Node {
    id: string;
    x: number;
    y: number;
}

export interface Connection {
    from: string;
    to: string;
}

interface ArcConnectionsState {
    nodes: Node[];
    connections: Connection[];
    bezierAngle: number;
    bezierAngleRandomness: number;
    connectionDistanceThreshold: number;
    connectionChance: number;
    angleFlipPercent: number;
    centerAngleThreshold: number;
    brushSize: number;
    brushDensity: number;
    nodeBaseSize: number;
    nodeSizeIncrement: number;
    nodeSizeRandomness: number;
    nodeColor: string;
    strokeColor: string;
    connectionOpacity: number;
    viewBoxAttr: string;
    addNode: (x: number, y: number) => void;
    clearNodes: () => void;
    setBezierAngle: (angle: number) => void;
    setBezierAngleRandomness: (randomness: number) => void;
    setConnectionDistanceThreshold: (threshold: number) => void;
    setConnectionChance: (chance: number) => void;
    setAngleFlipPercent: (percent: number) => void;
    setCenterAngleThreshold: (threshold: number) => void;
    setBrushSize: (size: number) => void;
    setBrushDensity: (density: number) => void;
    setNodeBaseSize: (size: number) => void;
    setNodeSizeIncrement: (increment: number) => void;
    setNodeSizeRandomness: (randomness: number) => void;
    setNodeColor: (color: string) => void;
    setStrokeColor: (color: string) => void;
    setConnectionOpacity: (opacity: number) => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
}

const ArcConnectionsContext = createContext<ArcConnectionsState | null>(null);

export function useArcConnections() {
    const context = useContext(ArcConnectionsContext);
    if (!context) {
        throw new Error(
            "useArcConnections must be used within ArcConnectionsProvider"
        );
    }
    return context;
}

interface ArcConnectionsProviderProps {
    children: React.ReactNode;
    initialBezierAngle?: number;
    initialBezierAngleRandomness?: number;
    initialConnectionDistanceThreshold?: number;
    initialConnectionChance?: number;
    initialAngleFlipPercent?: number;
    initialCenterAngleThreshold?: number;
    initialBrushSize?: number;
    initialBrushDensity?: number;
    initialNodeBaseSize?: number;
    initialNodeSizeIncrement?: number;
    initialNodeSizeRandomness?: number;
    initialNodeColor?: string;
    initialStrokeColor?: string;
    initialConnectionOpacity?: number;
}

export function ArcConnectionsProvider({
    children,
    initialBezierAngle = 45,
    initialBezierAngleRandomness = 0,
    initialConnectionDistanceThreshold = 500,
    initialConnectionChance = 100,
    initialAngleFlipPercent = 50,
    initialCenterAngleThreshold = 180,
    initialBrushSize = 50,
    initialBrushDensity = 1,
    initialNodeBaseSize = 8,
    initialNodeSizeIncrement = 2,
    initialNodeSizeRandomness = 0,
    initialNodeColor = "#ffffff",
    initialStrokeColor = "#4a9eff",
    initialConnectionOpacity = 1,
}: ArcConnectionsProviderProps) {
    const [nodes, setNodes] = useState<Node[]>([]);
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const history = useUndoRedo<Node[]>({ clone: cloneArray });

    const pushNodesSnapshot = useCallback(() => {
        history.push(nodesRef.current);
    }, [history]);

    const undo = useCallback(() => {
        history.undo(nodesRef.current, setNodes);
    }, [history]);

    const redo = useCallback(() => {
        history.redo(nodesRef.current, setNodes);
    }, [history]);

    const [bezierAngle, setBezierAngle] = useState(initialBezierAngle);
    const [bezierAngleRandomness, setBezierAngleRandomness] = useState(
        initialBezierAngleRandomness
    );
    const [connectionDistanceThreshold, setConnectionDistanceThreshold] =
        useState(initialConnectionDistanceThreshold);
    const [connectionChance, setConnectionChance] = useState(
        initialConnectionChance
    );
    const [angleFlipPercent, setAngleFlipPercent] = useState(
        initialAngleFlipPercent
    );
    const [centerAngleThreshold, setCenterAngleThreshold] = useState(
        initialCenterAngleThreshold
    );
    const [brushSize, setBrushSize] = useState(initialBrushSize);
    const [brushDensity, setBrushDensity] = useState(initialBrushDensity);
    const [nodeBaseSize, setNodeBaseSize] = useState(initialNodeBaseSize);
    const [nodeSizeIncrement, setNodeSizeIncrement] = useState(
        initialNodeSizeIncrement
    );
    const [nodeSizeRandomness, setNodeSizeRandomness] = useState(
        initialNodeSizeRandomness
    );
    const [nodeColor, setNodeColor] = useState(initialNodeColor);
    const [strokeColor, setStrokeColor] = useState(initialStrokeColor);
    const [connectionOpacity, setConnectionOpacity] = useState(
        initialConnectionOpacity
    );

    const viewBoxAttr = "0 0 1000 1000";
    const centerX = 500;
    const centerY = 500;

    const connections = useMemo(() => {
        if (nodes.length < 2) return [];
        const conns: Connection[] = [];

        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const node1 = nodes[i];
                const node2 = nodes[j];
                const dx = node2.x - node1.x;
                const dy = node2.y - node1.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance < connectionDistanceThreshold) {
                    // Calculate angle between (center - node1) and (node1 - node2)
                    const centerToNode1X = node1.x - centerX;
                    const centerToNode1Y = node1.y - centerY;
                    const node1ToNode2X = node2.x - node1.x;
                    const node1ToNode2Y = node2.y - node1.y;

                    // Calculate angle between vectors (direction 1: center->node1, node1->node2)
                    const dotProduct1 =
                        centerToNode1X * node1ToNode2X +
                        centerToNode1Y * node1ToNode2Y;
                    const mag1_1 = Math.sqrt(
                        centerToNode1X * centerToNode1X +
                            centerToNode1Y * centerToNode1Y
                    );
                    const mag1_2 = Math.sqrt(
                        node1ToNode2X * node1ToNode2X +
                            node1ToNode2Y * node1ToNode2Y
                    );

                    let angle1 = 180;
                    if (mag1_1 > 0 && mag1_2 > 0) {
                        const cosAngle = Math.max(
                            -1,
                            Math.min(1, dotProduct1 / (mag1_1 * mag1_2))
                        );
                        angle1 = Math.abs(
                            (Math.acos(cosAngle) * 180) / Math.PI
                        );
                    }

                    // Calculate angle between (center - node2) and (node2 - node1) for symmetry
                    const centerToNode2X = node2.x - centerX;
                    const centerToNode2Y = node2.y - centerY;
                    const node2ToNode1X = node1.x - node2.x;
                    const node2ToNode1Y = node1.y - node2.y;

                    const dotProduct2 =
                        centerToNode2X * node2ToNode1X +
                        centerToNode2Y * node2ToNode1Y;
                    const mag2_1 = Math.sqrt(
                        centerToNode2X * centerToNode2X +
                            centerToNode2Y * centerToNode2Y
                    );
                    const mag2_2 = Math.sqrt(
                        node2ToNode1X * node2ToNode1X +
                            node2ToNode1Y * node2ToNode1Y
                    );

                    let angle2 = 180;
                    if (mag2_1 > 0 && mag2_2 > 0) {
                        const cosAngle = Math.max(
                            -1,
                            Math.min(1, dotProduct2 / (mag2_1 * mag2_2))
                        );
                        angle2 = Math.abs(
                            (Math.acos(cosAngle) * 180) / Math.PI
                        );
                    }

                    // Use the minimum angle for symmetric behavior
                    const angle = Math.min(angle1, angle2);

                    if (angle <= centerAngleThreshold) {
                        // Deterministic hash function to decide if a connection should be made
                        const hash = (node1.id + node2.id)
                            .split("")
                            .reduce((acc, char) => {
                                return (
                                    ((acc << 5) - acc + char.charCodeAt(0)) | 0
                                );
                            }, 0);
                        // Use absolute value and modulo to get a value between 0-99
                        const hashValue = Math.abs(hash) % 100;

                        if (hashValue < connectionChance) {
                            conns.push({ from: node1.id, to: node2.id });
                        }
                    }
                }
            }
        }

        return conns;
    }, [
        nodes,
        connectionDistanceThreshold,
        connectionChance,
        centerAngleThreshold,
    ]);

    const addNode = (x: number, y: number) => {
        pushNodesSnapshot();
        if (brushSize <= 0 || brushDensity <= 0) {
            // If brush is disabled, add single node at exact position
            const newNode: Node = {
                id: `node-${Date.now()}-${Math.random()}`,
                x,
                y,
            };
            setNodes((prev) => [...prev, newNode]);
            return;
        }

        // Add nodes randomly within brush radius based on density
        const nodeCount = Math.max(1, Math.round(brushDensity));
        const newNodes: Node[] = [];

        for (let i = 0; i < nodeCount; i++) {
            // Generate random point within circle using uniform distribution
            const angle = Math.random() * 2 * Math.PI;
            const radius = Math.sqrt(Math.random()) * brushSize;
            const nodeX = x + Math.cos(angle) * radius;
            const nodeY = y + Math.sin(angle) * radius;

            newNodes.push({
                id: `node-${Date.now()}-${i}-${Math.random()}`,
                x: nodeX,
                y: nodeY,
            });
        }

        setNodes((prev) => [...prev, ...newNodes]);
    };

    const clearNodes = () => {
        if (nodesRef.current.length === 0) return;
        pushNodesSnapshot();
        setNodes([]);
    };

    // Helper functions for building SVG export
    const shouldFlipConnection = (fromId: string, toId: string): boolean => {
        const hash = (fromId + toId).split("").reduce((acc, char) => {
            return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
        }, 0);
        const hashValue = Math.abs(hash) % 100;
        return hashValue < angleFlipPercent;
    };

    const getBezierPath = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        fromId: string,
        toId: string
    ): string => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const lineAngle = Math.atan2(dy, dx);

        // Apply randomness to bezier angle
        let effectiveAngle = bezierAngle;
        if (bezierAngleRandomness > 0) {
            const hash = (fromId + toId).split("").reduce((acc, char) => {
                return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
            }, 0);
            const randomValue = (Math.abs(hash) % 2000) / 1000 - 1; // -1 to 1
            const angleVariation =
                bezierAngle * bezierAngleRandomness * randomValue;
            effectiveAngle = Math.max(
                0,
                Math.min(90, bezierAngle + angleVariation)
            );
        }

        const angleRad = (effectiveAngle * Math.PI) / 180;
        const flip = shouldFlipConnection(fromId, toId);

        const controlDistance = distance * 0.4;
        const cp1Angle = flip ? lineAngle - angleRad : lineAngle + angleRad;
        const cp2Angle = flip
            ? lineAngle + angleRad + Math.PI
            : lineAngle - angleRad + Math.PI;

        const cp1x = x1 + Math.cos(cp1Angle) * controlDistance;
        const cp1y = y1 + Math.sin(cp1Angle) * controlDistance;
        const cp2x = x2 + Math.cos(cp2Angle) * controlDistance;
        const cp2y = y2 + Math.sin(cp2Angle) * controlDistance;

        return `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    };

    const getNodeSize = (nodeId: string, connectionCount: number): number => {
        const baseSize = nodeBaseSize + connectionCount * nodeSizeIncrement;

        if (nodeSizeRandomness <= 0) {
            return baseSize;
        }

        const hash = nodeId.split("").reduce((acc, char) => {
            return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
        }, 0);
        const randomValue = (Math.abs(hash) % 2000) / 1000 - 1;
        const randomOffset = baseSize * nodeSizeRandomness * randomValue;

        return Math.max(1, baseSize + randomOffset);
    };

    const buildExportSvgString = (): string => {
        // Calculate node connection counts
        const nodeConnectionCounts: Record<string, number> = {};
        nodes.forEach((node) => {
            nodeConnectionCounts[node.id] = 0;
        });
        connections.forEach((conn) => {
            nodeConnectionCounts[conn.from] =
                (nodeConnectionCounts[conn.from] || 0) + 1;
            nodeConnectionCounts[conn.to] =
                (nodeConnectionCounts[conn.to] || 0) + 1;
        });

        // Build connections markup
        const connectionsMarkup = connections
            .map((conn) => {
                const fromNode = nodes.find((n) => n.id === conn.from);
                const toNode = nodes.find((n) => n.id === conn.to);
                if (!fromNode || !toNode) return "";

                const pathData = getBezierPath(
                    fromNode.x,
                    fromNode.y,
                    toNode.x,
                    toNode.y,
                    conn.from,
                    conn.to
                );

                return `    <path d="${pathData}" stroke="${strokeColor}" stroke-width="2" fill="none" opacity="${connectionOpacity}" />`;
            })
            .filter(Boolean)
            .join("\n");

        // Build nodes markup
        const nodesMarkup = nodes
            .map((node) => {
                const connectionCount = nodeConnectionCounts[node.id] || 0;
                const radius = getNodeSize(node.id, connectionCount);
                return `    <circle cx="${node.x}" cy="${node.y}" r="${radius}" fill="${nodeColor}" stroke="${strokeColor}" stroke-width="2" />`;
            })
            .join("\n");

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="connections">
${connectionsMarkup}
  </g>
  <g class="nodes">
${nodesMarkup}
  </g>
</svg>`;
    };

    const handleDownload = () => {
        if (nodes.length === 0) return;

        const svgString = buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `arc-connections-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            bezierAngle, bezierAngleRandomness, connectionDistanceThreshold,
            connectionChance, angleFlipPercent, centerAngleThreshold,
            brushSize, brushDensity, nodeBaseSize, nodeSizeIncrement,
            nodeSizeRandomness, nodeColor, strokeColor, connectionOpacity,
        }),
        [
            bezierAngle, bezierAngleRandomness, connectionDistanceThreshold,
            connectionChance, angleFlipPercent, centerAngleThreshold,
            brushSize, brushDensity, nodeBaseSize, nodeSizeIncrement,
            nodeSizeRandomness, nodeColor, strokeColor, connectionOpacity,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.bezierAngle != null) setBezierAngle(values.bezierAngle as number);
        if (values.bezierAngleRandomness != null) setBezierAngleRandomness(values.bezierAngleRandomness as number);
        if (values.connectionDistanceThreshold != null) setConnectionDistanceThreshold(values.connectionDistanceThreshold as number);
        if (values.connectionChance != null) setConnectionChance(values.connectionChance as number);
        if (values.angleFlipPercent != null) setAngleFlipPercent(values.angleFlipPercent as number);
        if (values.centerAngleThreshold != null) setCenterAngleThreshold(values.centerAngleThreshold as number);
        if (values.brushSize != null) setBrushSize(values.brushSize as number);
        if (values.brushDensity != null) setBrushDensity(values.brushDensity as number);
        if (values.nodeBaseSize != null) setNodeBaseSize(values.nodeBaseSize as number);
        if (values.nodeSizeIncrement != null) setNodeSizeIncrement(values.nodeSizeIncrement as number);
        if (values.nodeSizeRandomness != null) setNodeSizeRandomness(values.nodeSizeRandomness as number);
        if (values.nodeColor != null) setNodeColor(values.nodeColor as string);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.connectionOpacity != null) setConnectionOpacity(values.connectionOpacity as number);
    }, []);

    const { resetParameters: resetStoredParameters } = useToolParamPersistence({
        toolId: "arc-connections",
        defaultPreset: {
            bezierAngle: initialBezierAngle,
            bezierAngleRandomness: initialBezierAngleRandomness,
            connectionDistanceThreshold: initialConnectionDistanceThreshold,
            connectionChance: initialConnectionChance,
            angleFlipPercent: initialAngleFlipPercent,
            centerAngleThreshold: initialCenterAngleThreshold,
            brushSize: initialBrushSize,
            brushDensity: initialBrushDensity,
            nodeBaseSize: initialNodeBaseSize,
            nodeSizeIncrement: initialNodeSizeIncrement,
            nodeSizeRandomness: initialNodeSizeRandomness,
            nodeColor: initialNodeColor,
            strokeColor: initialStrokeColor,
            connectionOpacity: initialConnectionOpacity,
        },
        getPresetValues,
        applyPreset,
    });

    const resetParameters = useCallback(() => {
        history.clear();
        resetStoredParameters();
    }, [history, resetStoredParameters]);

    return (
        <ArcConnectionsContext.Provider
            value={{
                nodes,
                connections,
                bezierAngle,
                bezierAngleRandomness,
                connectionDistanceThreshold,
                connectionChance,
                angleFlipPercent,
                centerAngleThreshold,
                brushSize,
                brushDensity,
                nodeBaseSize,
                nodeSizeIncrement,
                nodeSizeRandomness,
                nodeColor,
                strokeColor,
                connectionOpacity,
                viewBoxAttr,
                addNode,
                clearNodes,
                setBezierAngle,
                setBezierAngleRandomness,
                setConnectionDistanceThreshold,
                setConnectionChance,
                setAngleFlipPercent,
                setCenterAngleThreshold,
                setBrushSize,
                setBrushDensity,
                setNodeBaseSize,
                setNodeSizeIncrement,
                setNodeSizeRandomness,
                setNodeColor,
                setStrokeColor,
                setConnectionOpacity,
                buildExportSvgString,
                handleDownload,
                applyPreset,
                getPresetValues,
                resetParameters,
                undo,
                redo,
                canUndo: history.canUndo,
                canRedo: history.canRedo,
            }}
        >
            {children}
        </ArcConnectionsContext.Provider>
    );
}
